/**
 * A QR code, drawn as inline SVG (DECISIONS D-013).
 *
 * ── No request, of any kind ─────────────────────────────────────────────────
 * There is no `<img>`, no `src`, no data URL and no canvas here — the matrix is
 * computed locally by `src/core/qr.ts` and painted as one `<path>`. The string
 * being encoded is a live onboarding secret, so handing it to a QR web service
 * to render would put a working credential in a third party's access log; and
 * an external request would break the offline cold boot D-009 pins.
 * `tests/e2e/pwa.spec.ts` asserts zero foreign requests and this keeps that
 * true by construction.
 *
 * ── Sized to be scannable, not to be pretty ─────────────────────────────────
 * The symbol scales to the full width of its container through the viewBox, so
 * a longer origin (which means a denser code) does not shrink the modules — it
 * just uses more of the same box. `shapeRendering="crispEdges"` turns off
 * antialiasing: a blurred module boundary is the difference between a scan that
 * takes a moment and one that never resolves, and this is being read off a
 * screen at an angle by whatever phone the surveyor owns.
 *
 * ── The quiet zone is not decoration ────────────────────────────────────────
 * The four-module light margin is part of the specification: a scanner uses it
 * to find the symbol at all. It is in the viewBox and the white backing rect
 * covers it, so the code stays scannable on any card colour.
 *
 * ── Legibility (D-011) ──────────────────────────────────────────────────────
 * There is no text in here, and deliberately no `<title>` element either — the
 * accessible name is an `aria-label`, so the audit's text walker has nothing to
 * measure and screen readers still announce what it is. Everything the office
 * needs to READ sits outside this component, at full helper size.
 */

import { useMemo, type ReactNode } from 'react';

import { qrMatrix, qrPath, qrViewBoxSize } from '../../core/qr';

export interface QrCodeProps {
  /** The URL to encode. Never rendered as text anywhere. */
  value: string;
  /** Accessible name, e.g. "Setup code for Dave Reyes". */
  label: string;
}

export function QrCode({ value, label }: QrCodeProps): ReactNode {
  // Encoding is Reed-Solomon over a few hundred bytes; cheap, but it runs on
  // every keystroke elsewhere on this screen if it is not memoised.
  const drawn = useMemo(() => {
    try {
      const matrix = qrMatrix(value);
      return { side: qrViewBoxSize(matrix), path: qrPath(matrix) };
    } catch {
      return null;
    }
  }, [value]);

  if (drawn === null) {
    // A code that cannot be drawn must not render as an empty white box the
    // office would stand there scanning.
    return (
      <p className="ruleline" data-legibility="helper">
        This code could not be drawn on screen. Try making a new one.
      </p>
    );
  }

  return (
    <svg
      className="qr"
      role="img"
      aria-label={label}
      viewBox={`0 0 ${String(drawn.side)} ${String(drawn.side)}`}
      shapeRendering="crispEdges"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* White under the whole symbol INCLUDING the quiet zone: a QR read off a
          coloured card is a QR that may not read at all. */}
      <rect x="0" y="0" width={drawn.side} height={drawn.side} fill="#ffffff" />
      <path d={drawn.path} fill="#000000" />
    </svg>
  );
}
